import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatError } from "@/lib/api";

export default function AdminShipping() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/shipping-config");
      setCfg(data);
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        standard_cost: parseFloat(cfg.standard_cost),
        free_threshold: parseFloat(cfg.free_threshold),
        free_shipping_enabled: !!cfg.free_shipping_enabled,
        bulky_surcharge: parseFloat(cfg.bulky_surcharge || 0),
        currency: cfg.currency || "eur",
      };
      await api.put("/admin/shipping-config", payload);
      toast.success("Impostazioni spedizione salvate");
    } catch (e) {
      toast.error("Errore", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <AdminLayout title="Spedizioni" back="/admin"><p className="text-slate-500">Caricamento...</p></AdminLayout>;

  return (
    <AdminLayout title="Spedizioni" back="/admin">
      <div className="bg-white border border-border rounded-2xl p-5 max-w-2xl space-y-4">
        <p className="text-sm text-slate-500">
          Configura i costi di spedizione applicati automaticamente al checkout. Nessuna integrazione corriere è attiva – la soglia gratuita è calcolata sul subtotale prodotti.
        </p>

        <div>
          <Label>Costo spedizione standard (€) *</Label>
          <Input
            data-testid="shipping-cost"
            type="number"
            step="0.01"
            value={cfg.standard_cost}
            onChange={(e) => set("standard_cost", e.target.value)}
            className="mt-1 rounded-xl"
          />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Switch
            data-testid="free-enabled"
            checked={!!cfg.free_shipping_enabled}
            onCheckedChange={(v) => set("free_shipping_enabled", v)}
          />
          <Label>Attiva spedizione gratuita sopra soglia</Label>
        </div>

        <div>
          <Label>Soglia spedizione gratuita (€)</Label>
          <Input
            data-testid="free-threshold"
            type="number"
            step="0.01"
            value={cfg.free_threshold}
            onChange={(e) => set("free_threshold", e.target.value)}
            disabled={!cfg.free_shipping_enabled}
            className="mt-1 rounded-xl"
          />
          <p className="text-xs text-slate-500 mt-1">
            Se il subtotale prodotti raggiunge o supera questa soglia, la spedizione è gratuita.
          </p>
        </div>

        <div>
          <Label>Supplemento prodotti voluminosi (€)</Label>
          <Input
            data-testid="bulky-surcharge"
            type="number"
            step="0.01"
            value={cfg.bulky_surcharge || 0}
            onChange={(e) => set("bulky_surcharge", e.target.value)}
            className="mt-1 rounded-xl"
          />
          <p className="text-xs text-slate-500 mt-1">
            Aggiunto al costo di spedizione se almeno un prodotto è contrassegnato come "voluminoso" nella scheda prodotto.
          </p>
        </div>

        <Button
          data-testid="save-shipping-btn"
          onClick={save}
          disabled={saving}
          className="rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-bold"
        >
          {saving ? "Salvataggio..." : "Salva impostazioni"}
        </Button>
      </div>
    </AdminLayout>
  );
}
